/**
 * Position wrapper that lets a fixed overlay be dragged around on touch
 * devices. Each overlay gets a stable `id` (used as the localStorage key) and
 * a `defaultAnchor` describing where it should sit before the user has
 * dragged it. Anchors mirror CSS — pass `top/bottom` + `left/right` in
 * pixels, or `centerX` for horizontally-centered overlays. Once the user
 * drags, we switch to absolute pixel positioning and persist.
 *
 * Drag mechanics:
 *   - Activates only when `enabled` (default: caller passes `useIsTouch()`).
 *   - Capture pointer on pointerdown; threshold of `DRAG_THRESHOLD_PX` of
 *     movement before we treat it as a drag (so a tap on a button inside
 *     still fires its onClick).
 *   - If the pointerdown target matches `nonDragSelector` (e.g. inputs and
 *     scrollable feeds), bail early so the underlying control keeps native
 *     behavior.
 *   - On a real drag, suppress the trailing click via a one-shot capture
 *     listener so wrapped buttons don't fire after a fingertip pan.
 *
 * The overlay's contents must NOT carry their own `position: fixed/absolute`
 * — the wrapper owns positioning. Inner styling (size, padding, transforms
 * that don't include translate) is fine.
 */
import React, {
  type CSSProperties,
  type ReactNode,
  useEffect,
  useRef,
  useState,
} from "react";

export interface DraggableAnchor {
  /** Pixels from the viewport left edge. */
  left?: number;
  /** Pixels from the viewport right edge. */
  right?: number;
  /** Pixels from the viewport top edge. */
  top?: number;
  /** Pixels from the viewport bottom edge. Combined with safe-area-inset-bottom. */
  bottom?: number;
  /** Horizontally center the overlay (overrides left/right). */
  centerX?: boolean;
  /** Respect the iOS safe-area-inset-top when top is set. */
  safeAreaTop?: boolean;
  /** Respect the iOS safe-area-inset-bottom when bottom is set. */
  safeAreaBottom?: boolean;
}

interface PixelPos {
  left: number;
  top: number;
}

interface DraggableOverlayProps {
  /** Stable id used as the localStorage key for the persisted position. */
  id: string;
  /** Initial position when no persisted pos exists. */
  defaultAnchor: DraggableAnchor;
  /** Gate dragging on touch (`useIsTouch()`). When false, behaves as a
   *  passive positioned wrapper. */
  enabled?: boolean;
  /** Stack order for the overlay. */
  zIndex?: number;
  /** CSS selector for elements inside the overlay that should NOT initiate
   *  drag (e.g. `"input, textarea, [data-no-drag]"`). The element still
   *  responds to taps; only drag is suppressed. */
  nonDragSelector?: string;
  children: ReactNode;
}

const DRAG_THRESHOLD_PX = 6;
const LS_PREFIX = "solblox.draggable.";
/** Uniform shrink applied to every HUD overlay on touch so the small-screen
 *  HUD stops feeling cramped/stacked. Tune here to scale the whole mobile HUD. */
const MOBILE_SCALE = 0.78;
/** Always keep this many CSS pixels of the overlay reachable on every edge
 *  so the user can grab it back even after a rotation. */
const VIEWPORT_MARGIN = 32;

export function DraggableOverlay({
  id,
  defaultAnchor,
  enabled = true,
  zIndex,
  nonDragSelector,
  children,
}: DraggableOverlayProps): JSX.Element {
  const [pixel, setPixel] = useState<PixelPos | null>(() => loadPersisted(id));
  const pixelRef = useRef<PixelPos | null>(pixel);
  pixelRef.current = pixel;

  const rootRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<{
    pointerId: number;
    startX: number;
    startY: number;
    startLeft: number;
    startTop: number;
    moved: boolean;
  } | null>(null);

  const onPointerDown = (e: React.PointerEvent): void => {
    if (!enabled) return;
    const target = e.target as HTMLElement;
    if (nonDragSelector && target.closest(nonDragSelector)) return;
    const el = rootRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    try {
      el.setPointerCapture(e.pointerId);
    } catch {
      // Some platforms reject capture for non-primary pointers — ignore.
    }
    dragRef.current = {
      pointerId: e.pointerId,
      startX: e.clientX,
      startY: e.clientY,
      startLeft: rect.left,
      startTop: rect.top,
      moved: false,
    };
  };

  const onPointerMove = (e: React.PointerEvent): void => {
    const d = dragRef.current;
    if (!d || e.pointerId !== d.pointerId) return;
    const dx = e.clientX - d.startX;
    const dy = e.clientY - d.startY;
    if (!d.moved && Math.hypot(dx, dy) < DRAG_THRESHOLD_PX) return;
    if (!d.moved) {
      d.moved = true;
      // Switching from anchor → pixel mode using rect.left/top preserves
      // visual position exactly.
      setPixel({ left: d.startLeft, top: d.startTop });
    }
    const el = rootRef.current;
    const w = el ? el.offsetWidth : 0;
    const h = el ? el.offsetHeight : 0;
    setPixel(
      clampToViewport({ left: d.startLeft + dx, top: d.startTop + dy }, w, h),
    );
  };

  const onPointerUp = (e: React.PointerEvent): void => {
    const d = dragRef.current;
    if (!d || e.pointerId !== d.pointerId) return;
    const el = rootRef.current;
    try {
      el?.releasePointerCapture(e.pointerId);
    } catch {
      // ignore
    }
    if (d.moved) {
      const p = pixelRef.current;
      if (p) savePersisted(id, p);
      suppressNextClick();
    }
    dragRef.current = null;
  };

  // Re-clamp on viewport changes (rotation, browser-chrome show/hide).
  useEffect(() => {
    if (!enabled) return;
    const onResize = (): void => {
      const cur = pixelRef.current;
      if (!cur) return;
      const el = rootRef.current;
      const w = el ? el.offsetWidth : 0;
      const h = el ? el.offsetHeight : 0;
      setPixel(clampToViewport(cur, w, h));
    };
    window.addEventListener("resize", onResize);
    window.addEventListener("orientationchange", onResize);
    return () => {
      window.removeEventListener("resize", onResize);
      window.removeEventListener("orientationchange", onResize);
    };
  }, [enabled]);

  const dragged = pixel != null;
  const positionStyle: CSSProperties = dragged
    ? { left: pixel.left, top: pixel.top }
    : anchorToStyle(defaultAnchor);

  // Scale the whole overlay down on touch. Origin is the anchored corner so the
  // panel shrinks toward where it's pinned (no drift off-screen); once dragged
  // it's pinned by top-left, so scale from there. centerX keeps its -50% shift.
  const scale = enabled ? MOBILE_SCALE : 1;
  const originX = dragged
    ? "left"
    : defaultAnchor.right != null
      ? "right"
      : defaultAnchor.centerX
        ? "center"
        : "left";
  const originY = dragged ? "top" : defaultAnchor.bottom != null ? "bottom" : "top";
  const transforms: string[] = [];
  if (!dragged && defaultAnchor.centerX) transforms.push("translateX(-50%)");
  if (scale !== 1) transforms.push(`scale(${scale})`);
  const transform = transforms.length > 0 ? transforms.join(" ") : undefined;

  return (
    <div
      ref={rootRef}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      style={{
        position: "fixed",
        ...positionStyle,
        transform,
        transformOrigin: scale !== 1 ? `${originX} ${originY}` : undefined,
        // `touch-action: none` on the wrapper stops the browser hijacking the
        // pointer for scroll/zoom while we're dragging. We selectively
        // re-enable it on scrollable children via inline style if needed.
        touchAction: enabled ? "none" : undefined,
        zIndex,
      }}
    >
      {children}
    </div>
  );
}

function anchorToStyle(a: DraggableAnchor): CSSProperties {
  const style: CSSProperties = {};
  if (a.centerX) {
    // The -50% translate is composed with the mobile scale in the wrapper so
    // both transforms coexist (a second `transform` would clobber the first).
    style.left = "50%";
  } else {
    if (a.left != null) style.left = a.left;
    if (a.right != null) style.right = a.right;
  }
  if (a.top != null) {
    style.top = a.safeAreaTop
      ? `calc(env(safe-area-inset-top) + ${a.top}px)`
      : a.top;
  }
  if (a.bottom != null) {
    style.bottom = a.safeAreaBottom
      ? `calc(env(safe-area-inset-bottom) + ${a.bottom}px)`
      : a.bottom;
  }
  return style;
}

function clampToViewport(p: PixelPos, w: number, _h: number): PixelPos {
  if (typeof window === "undefined") return p;
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  // At least VIEWPORT_MARGIN of the overlay must remain on each edge so the
  // user can drag it back. We allow the overlay to extend off-screen up to
  // (size - VIEWPORT_MARGIN) on the trailing edge. Height-based clamp uses
  // viewport height directly so the top-edge marker (where the drag handle
  // usually sits) always stays reachable; the trailing bottom edge can spill.
  const minLeft = VIEWPORT_MARGIN - w;
  const maxLeft = vw - VIEWPORT_MARGIN;
  const minTop = 0;
  const maxTop = vh - VIEWPORT_MARGIN;
  return {
    left: Math.min(Math.max(p.left, minLeft), maxLeft),
    top: Math.min(Math.max(p.top, minTop), maxTop),
  };
}

function loadPersisted(id: string): PixelPos | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(LS_PREFIX + id);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<PixelPos>;
    if (typeof parsed.left === "number" && typeof parsed.top === "number") {
      return { left: parsed.left, top: parsed.top };
    }
  } catch {
    // ignore
  }
  return null;
}

function savePersisted(id: string, p: PixelPos): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(LS_PREFIX + id, JSON.stringify(p));
  } catch {
    // quota / private mode — non-fatal
  }
}

/** Swallow the click event that fires after a drag-pointerup so wrapped
 *  buttons don't trigger when the user was actually panning. */
function suppressNextClick(): void {
  if (typeof window === "undefined") return;
  const handler = (ev: MouseEvent): void => {
    ev.preventDefault();
    ev.stopPropagation();
    window.removeEventListener("click", handler, true);
  };
  window.addEventListener("click", handler, true);
  // Defensive cleanup if no click ever arrives (e.g. drag off a non-button).
  window.setTimeout(() => window.removeEventListener("click", handler, true), 250);
}
